import dayjs from "dayjs";

import { isHabitScheduledOn, type HabitSchedule, type HabitWeekday } from "../../entities/Habit";

// TRE-1575 — ONE definition of "a scheduled habit day" and of the completion rate, shared by the
// mobile habit-detail sheet and both admin compliance views. Keeping this in the domain layer
// guarantees the client and the trainer always see the same percentages.

const DATE_FORMAT = "YYYY-MM-DD";

export type HabitScheduledRangeParams = {
  schedule: HabitSchedule | null | undefined;
  /** The habit's start (client_habit.startDate) — days before it are never "scheduled". */
  startDate?: string | null;
  /** Inclusive YYYY-MM-DD range bounds. */
  from: string;
  to: string;
};

/**
 * Every date in [from..to] the habit was actually planned for: falls on a scheduled weekday
 * and not before the habit's startDate.
 *
 * Schedule semantics match the habit model exactly:
 *  - a schedule with weekdays → only those weekdays (via isHabitScheduledOn),
 *  - an EMPTY schedule ({ daysOfWeek: [] }) → NO scheduled days (isHabitScheduledOn returns false,
 *    UI shows "Brak dni"); yields 0 scheduled days, so the completion rate is null,
 *  - a NULL schedule (defensive only — habits always carry one) → every day.
 */
export const habitScheduledDatesInRange = ({
  schedule,
  startDate,
  from,
  to,
}: HabitScheduledRangeParams): string[] => {
  const startBound = startDate ? startDate.slice(0, 10) : null;
  const first = dayjs(from, DATE_FORMAT).startOf("day");
  const last = dayjs(to, DATE_FORMAT).startOf("day");

  if (!first.isValid() || !last.isValid() || last.isBefore(first)) {
    return [];
  }

  const dates: string[] = [];
  const span = last.diff(first, "day");

  for (let offset = 0; offset <= span; offset += 1) {
    const day = first.add(offset, "day");
    const dateStr = day.format(DATE_FORMAT);
    if (startBound != null && dateStr < startBound) {
      continue;
    }
    if (schedule && !isHabitScheduledOn(schedule, day.day() as HabitWeekday)) {
      continue;
    }
    dates.push(dateStr);
  }

  return dates;
};

export type HabitCompletionRate = {
  /** Scheduled days that have a completed entry. */
  completed: number;
  /** Days the habit was planned for in the window. */
  scheduled: number;
  /** Rounded percentage, or null when nothing was scheduled (avoid a misleading 0%). */
  rate: number | null;
};

/**
 * Completion rate measured against SCHEDULED days (never calendar days): the numerator only
 * counts completions that fall on a scheduled day, so off-schedule extras can't exceed 100%.
 */
export const calculateHabitCompletionRate = (
  scheduledDates: string[],
  completedDates: Iterable<string>,
): HabitCompletionRate => {
  const completedSet = new Set<string>();
  for (const date of completedDates) {
    completedSet.add(date.slice(0, 10));
  }

  const completed = scheduledDates.filter((date) => completedSet.has(date)).length;
  const scheduled = scheduledDates.length;

  return {
    completed,
    scheduled,
    rate: scheduled > 0 ? Math.round((completed / scheduled) * 100) : null,
  };
};
